import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { withTranslation } from "react-i18next";
import Table from "components/table";
import Select, { components } from "react-select";
import styles from "./Leaves.module.scss";
import "./Leaves.scss";
import { Link } from "react-router-dom";
import Empty from "components/Empty";
import useRemoveCache from "hooks/useRemoveCache";
import { Fragment } from "react";
import Loader from "components/Loader";
import moment from "moment/moment";
import { setStorage, getStorage, encodeId } from "utils";
import useHandleApiError from "hooks/useHandleApiError";
import {
  archiveLeave,
  useStaffLeaveListing,
  updateLeaveStatus,
  cancelLeave,
} from "repositories/leave-repository";
import { useWarningDetails } from "repositories/office-repository";
import constants from "../../../constants";
import CustomModal from "components/CustomModal";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import RedAlertInfo from "../../../assets/images/red_alert_info.svg";
import CancellationModal from "../StaffListingLeaves/component/CancellationModal";
import SortDown from "../../../assets/images/pms-icons/sort_down.svg";
import SortUp from "../../../assets/images/pms-icons/sort_up.svg";
import { leaveTags } from "../StaffListingLeaves";
import RejectionModal from "../StaffListingTimesheet/component/RejectionModal";

const PAGE_SIZE = 6;
const WORD_LIMIT = 60;

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor:
      state.isFocused || state.isSelected ? "transparent" : "transparent",
    color: "#333",
    cursor: "pointer",
  }),
  control: (provided) => ({
    ...provided,
    borderColor: "#ccc",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#ccc",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "transparent",
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    fontSize: "14px",
    color: "#77928b",
    fontWeight: 500,
  }),
};

const leaveStatusList = [
  { value: 1, label: "Current Leaves" },
  { value: 2, label: "Past Leaves" },
  { value: 3, label: "Cancelled Leaves" },
  { value: 4, label: "Rejected Leaves" },
  { value: 5, label: "Archived Leaves" },
];

const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="ch-checkbox c-field all-event-checkbox m-0">
        <label>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => {}}
            onClick={(e) => e.stopPropagation()}
          />
          <span>{props.label}</span>
        </label>
      </div>
    </components.Option>
  );
};

const MultiValue = (props) => {
  const { getValue } = props;
  const selectedValues = getValue();

  if (selectedValues.length === 1) {
    return <components.MultiValue {...props} />;
  }

  return null;
};

const CustomValueContainer = ({ children, ...props }) => {
  const selectedValues = props.getValue();

  if (selectedValues.length > 1) {
    return (
      <components.ValueContainer {...props}>
        <div className={styles.multiple_selected}>Multiple Selected</div>
      </components.ValueContainer>
    );
  }

  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  );
};

const NoRemove = () => null;

// Dynamically creates a complete separate <tr> above the target row using a React Portal
const CancellationRejectBanner = ({ row, t, handlePopup }) => {
  const tdRef = React.useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (!row?.isCancellationReject) return;

    if (tdRef.current) {
      const currentTr = tdRef.current.closest("tr");
      if (currentTr && currentTr.parentNode) {
        // Create a completely separate TR dynamically
        const newTr = document.createElement("tr");

        // 👇 ADD THIS LINE: Attach your custom CSS class to the new row
        newTr.classList.add("staffLeaveCancellationExpandRow");

        const newTd = document.createElement("td");

        // Spans all 8 columns seamlessly
        newTd.colSpan = 8;
        newTd.style.padding = "0";
        newTd.style.border = "none";
        newTr.appendChild(newTd);

        // Insert exactly above the current data row
        currentTr.parentNode.insertBefore(newTr, currentTr);
        setPortalTarget(newTd);

        // Cleanup: remove the injected row when unmounting to keep the table clean
        return () => {
          if (newTr.parentNode) {
            newTr.parentNode.removeChild(newTr);
          }
        };
      }
    }
  }, [row?.isCancellationReject]);

  if (!row?.isCancellationReject) return null;

  const bannerContent = (
    <div className={`${styles.red_info_container} m-0 mb-2`}>
      <div className={styles.info_img_text}>
        <img src={RedAlertInfo} alt="RedAlertInfo" />
        <p className={styles.info_text}>
          {t("staffLeaves.yourLeaveCancellationRequest")}
        </p>
      </div>
      <p className={styles.click_text} onClick={() => handlePopup(row)}>
        {t("staffLeaves.clickForReason")}
      </p>
    </div>
  );

  return (
    <>
      {/* Hidden reference marker inside the normal column */}
      <span ref={tdRef} style={{ display: "none" }}></span>
      {/* Render the actual banner markup into the newly injected full-width TR */}
      {portalTarget ? createPortal(bannerContent, portalTarget) : null}
    </>
  );
};

const Leaves = ({ t, officeId, userId }) => {
  const staffLeaveTypeStatus = constants.LeaveTypeStatus;
  const [staffLeavelist, setStaffLeavelist] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [isChangesModalOpen, setIsChangesModalOpen] = useState("");
  const [pageNumber, setPageNumber] = useState(() => {
    const cacheValue = getStorage(constants.Leave.cache.staffLeavelisting);
    return cacheValue?.pageNumber || 1;
  });
  const [selectedLeaveStatuses, setSelectedLeaveStatuses] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [sortingList, setSortingList] = useState({
    sortByFromDateAscending: null,
    sortByToDateAscending: null,
  });
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [year, setYear] = useState(() => {
    const cacheValue = getStorage(constants.Leave.cache.staffLeavelisting);
    return cacheValue?.year || new Date().getFullYear();
  });
  const history = useHistory();
  const {
    data,
    error: isError,
    isLoading,
    refetch,
    isFetching,
  } = useStaffLeaveListing(
    officeId,
    userId,
    year,
    pageNumber,
    PAGE_SIZE,
    selectedLeaveStatuses,
    sortingList.sortByFromDateAscending,
    sortingList.sortByToDateAscending,
  );
  const { data: warningData } = useWarningDetails(officeId);

  let totalItems = data?.pagination?.totalPages * PAGE_SIZE;
  const leaveWarningData = {
    leavewarning: warningData?.showWarningMessageForLeaveRequest,
    numberofdays: warningData?.leaveRequestNoticePeriod,
    numberofdaysnotice:
      warningData?.showWarningMessageForLeaveRequestWhenDurationIsGreaterThan,
  };

  useHandleApiError(isLoading, isFetching, isError);
  const resetModalState = () => {
    setIsChangesModalOpen("");
    setShowLoader(false);
  };
  const handleSort = (keyName, value) => {
    setSortingList((prevState) => ({
      ...Object.keys(prevState).reduce((acc, key) => {
        acc[key] = key === keyName ? value : null;
        return acc;
      }, {}),
    }));
  };
  const applyArchiveActions = async (isArchived, reason) => {
    if (showLoader) return;
    setShowLoader(true);
    try {
      const params = {
        leaveId: selectedLeave?.id,
        isArchived: isArchived,
        Comment: reason,
      };
      let res = await archiveLeave(params);
      if (res.status) {
        toast.success(res.message);
        refetch();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      resetModalState();
    }
  };

  const withdrawLeaves = async (reason) => {
    if (showLoader) return;
    setShowLoader(true);
    try {
      const params = {
        id: selectedLeave?.id.toString(),
        Comment: reason,
        IsStaff: true,
        IsWithdrawn: true,
      };
      let res = await updateLeaveStatus(params);
      if (res.status) {
        toast.success(res.message);
        refetch();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      resetModalState();
    }
  };

  const staffCancelLeaves = async (reason) => {
    if (showLoader) return;
    setShowLoader(true);
    try {
      const params = {
        id: selectedLeave?.id,
        Comment: reason,
        IsStaff: true,
        IsCancel: true,
        officeId: officeId,
      };
      let res = await cancelLeave(params);
      if (res.status) {
        toast.success(res.message);
        refetch();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      resetModalState();
    }
  };

  const menuOptions = [
    {
      text: "Edit",
      onclick: (e, row) => {
        history.push({
          pathname: constants.routes.staff.editLeaves.replace(
            ":officeId",
            encodeId(officeId),
          ),
          state: { ...row },
        });
      },
    },
    {
      text: "Withdraw",
      onclick: (e, row) => {
        setSelectedLeave(row);
        setIsChangesModalOpen(leaveTags.withdrawn);
      },
    },
    {
      text: "Archive",
      onclick: (e, row) => {
        setSelectedLeave(row);
        setIsChangesModalOpen(leaveTags.archive);
      },
    },
    {
      text: "Unarchive",
      onclick: (e, row) => {
        setSelectedLeave(row);
        setIsChangesModalOpen(leaveTags.unarchive);
      },
    },
    {
      text: "Cancel",
      onclick: (e, row) => {
        setSelectedLeave(row);
        setIsChangesModalOpen(leaveTags.cancel);
      },
    },
    {
      text: "View History Log",
      onclick: (e, row) => {
        history.push({
          pathname: constants.routes.staff.leavesHistoryLogs.replace(
            ":leaveId",
            encodeId(row?.id),
          ),
          state: { ...row },
        });
      },
    },
  ];

  useEffect(() => {
    setStorage(constants.Leave.cache.staffLeavelisting, {
      pageNumber: pageNumber,
      year: year,
    });
  }, [pageNumber, year]);

  useEffect(() => {
    if (data?.data) {
      setStaffLeavelist(data.data?.leave);
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [data]);

  const customizeText = (text) => {
    if (!text) return "--";

    if (text?.length < WORD_LIMIT) {
      return text;
    }
    return text.slice(0, WORD_LIMIT).concat("...");
  };

  useRemoveCache([], constants.Leave.cache.staffLeavelisting);

  const currentYear = new Date().getFullYear();
  const yearsList = Array.from({ length: 10 }, (_, i) => {
    const yearValue = currentYear + 1 - i;
    return { label: yearValue, value: yearValue };
  });

  const updatePageNumber = (page) => {
    setPageNumber(page);
  };

  const handleSelectedYear = (item) => {
    setYear(item?.value);
    updatePageNumber(1);
  };

  const handleStaffLeaveListStatus = (statusId) => (
    <span>{constants.statusLabelMap[statusId] || "--"}</span>
  );

  const handleStaffLeaveTypeStatus = (leaveType) => (
    <>
      {leaveType === staffLeaveTypeStatus[1]?.value && (
        <span>{t("staffLeaves.casual")}</span>
      )}
      {leaveType === staffLeaveTypeStatus[2]?.value && (
        <span>{t("staffLeaves.medical")}</span>
      )}
      {leaveType === staffLeaveTypeStatus[3]?.value && (
        <span>{t("staffLeaves.vacation")}</span>
      )}
    </>
  );

  const handlePopup = (row) => {
    if (confirmPopup) {
      setSelectedLeave(null);
      setConfirmPopup(false);
    } else {
      setConfirmPopup(true);
      setSelectedLeave(row);
    }
  };

  const columns = [
    {
      attrs: { datatitle: t("staffLeaves.from") },
      dataField: "from",
      text: t("staffLeaves.from"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <CancellationRejectBanner row={row} t={t} handlePopup={handlePopup} />
          <span>{row.fromDate ? moment(row.fromDate).format("ll") : "--"}</span>
        </Fragment>
      ),
      sort: true,
      sortCaret: (order, column) => {
        if (order === "asc" || !order)
          return (
            <span onClick={() => handleSort("sortByFromDateAscending", true)}>
              <img src={SortDown} alt="sort_down" />
            </span>
          );
        else if (order === "desc")
          return (
            <span onClick={() => handleSort("sortByFromDateAscending", false)}>
              <img src={SortUp} alt="sort_up" />
            </span>
          );
        return null;
      },
    },
    {
      attrs: { datatitle: t("staffLeaves.to") },
      dataField: "to",
      text: t("staffLeaves.to"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <span>{row.toDate ? moment(row.toDate).format("ll") : "--"}</span>
        </Fragment>
      ),
      sort: true,
      sortCaret: (order, column) => {
        if (order === "asc" || !order)
          return (
            <span onClick={() => handleSort("sortByToDateAscending", true)}>
              <img src={SortDown} alt="sort_down" />
            </span>
          );
        else if (order === "desc")
          return (
            <span onClick={() => handleSort("sortByToDateAscending", false)}>
              <img src={SortUp} alt="sort_up" />
            </span>
          );
        return null;
      },
    },
    {
      attrs: { datatitle: t("staffLeaves.duration") },
      dataField: "duration",
      text: t("staffLeaves.duration"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <span>{row.duration ? row.duration : "--"}</span>
        </Fragment>
      ),
    },
    {
      attrs: { datatitle: t("staffLeaves.leaveType") },
      dataField: "leaveType",
      text: t("staffLeaves.leaveType"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <span>
            {row?.leaveType ? handleStaffLeaveTypeStatus(row.leaveType) : "--"}
          </span>
        </Fragment>
      ),
    },
    {
      attrs: { datatitle: t("staffLeaves.status") },
      dataField: "status",
      text: t("staffLeaves.status"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <span>
            {row?.status ? handleStaffLeaveListStatus(row.status) : "--"}
          </span>
        </Fragment>
      ),
    },
    {
      attrs: { datatitle: t("staffLeaves.reason") },
      dataField: "counter",
      text: t("staffLeaves.reasonForLeave"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          <div className="status-div">
            <span className="status">{customizeText(row.description)}</span>
            {row?.description?.length >= WORD_LIMIT && (
              <span
                className="link-btn"
                onClick={() => {
                  setSelectedReason(row.description);
                }}
              >
                Read More
              </span>
            )}
          </div>
        </Fragment>
      ),
    },
    {
      attrs: { datatitle: t("staffLeaves.reason") },
      dataField: "reasonForRejection",
      text: t("staffLeaves.reason"),
      formatter: (cellContent, row, rowIndex) => (
        <Fragment>
          {row?.comment && (
            <div className="status-div">
              <span className="status">{customizeText(row?.comment)}</span>
              {row?.description?.length >= WORD_LIMIT && (
                <span
                  className="link-btn"
                  onClick={() => {
                    setSelectedReason(row.description);
                  }}
                >
                  Read More
                </span>
              )}
            </div>
          )}
        </Fragment>
      ),
    },
    {
      attrs: {
        datatitle: t(
          "patientAppointmentMembers.appointmentTableHeading.action",
        ),
      },
      dataField: "action",
      text: t("patientAppointmentMembers.appointmentTableHeading.action"),
      formatter: (cellContent, row) => {
        const isPastDate = moment(row?.fromDate).isBefore(
          moment().startOf("day"),
        );
        const dynamicMenuOptions = menuOptions.filter((option) => {
          switch (option.text) {
            case "Archive":
              return !row.archiveStatus;
            case "Unarchive":
              return row.archiveStatus;
            case "Withdraw":
            case "Edit":
              return row.status === 1 && !isPastDate;
            case "Cancel":
              return row.status === 2 && !isPastDate;
            default:
              return true;
          }
        });
        return (
          <UncontrolledDropdown>
            <DropdownToggle caret={false} tag="div" className="cursor-pointer">
              <div className="ellipsis-icon">
                <ThreeDotsVertical />
              </div>
            </DropdownToggle>
            <DropdownMenu end className={styles["office-dropdown-menu"]}>
              {dynamicMenuOptions.map((option, index) => (
                <DropdownItem
                  key={index}
                  className={styles["office-dropdown-item"]}
                >
                  <p className="m-0" onClick={(e) => option?.onclick(e, row)}>
                    {option?.text}
                  </p>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const renderListing = () => (
    <div className="table-td-last-50 leaves-table shadow-responsive">
      {!!staffLeavelist?.length ? (
        <Table
          keyField="id"
          data={staffLeavelist}
          columns={columns}
          handlePagination={(e) => setPageNumber(e)}
          pageNumber={pageNumber}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          wrapperClassName="staff-leave-wrapper"
          tableContainerClassName="staff-leave-container"
          paginationClassName="staff-leave-pagination"
          tableClassName="staff-leave-table"
        />
      ) : (
        <Empty Message={t("staffLeaves.noLeavesAvailable")} />
      )}
    </div>
  );

  const handleOfficeChange = (selectedOptions) => {
    setPageNumber(1);
    const selectedValues = (selectedOptions || []).map(
      (option) => option.value,
    );
    setSelectedLeaveStatuses(selectedValues);
  };

  return (
    <>
      <div className={"leaves-page " + styles["leaves-page"]}>
        {isLoading && <Loader />}
        {leaveWarningData.leavewarning && (
          <div className={styles["alert-box"]}>
            <div className={styles["alert-img"]}>
              <img
                src={require("assets/images/alert-circle-black.svg").default}
                alt="icon"
              />
            </div>
            <div>
              {t("staffLeaves.alertMessage", {
                days: leaveWarningData.numberofdaysnotice,
                month: leaveWarningData.numberofdays / 30,
              })}
            </div>
          </div>
        )}
        <div className="d-md-flex justify-content-between align-items-center leaves_sticky_header">
          <div className="leaves_select_wrapper">
            <div className="leaves_select_container">
              <Select
                className="react-select-container w-25"
                classNamePrefix="react-select"
                options={leaveStatusList}
                value={leaveStatusList?.filter((option) =>
                  (selectedLeaveStatuses || []).includes(option.value),
                )}
                onChange={handleOfficeChange}
                isMulti
                closeMenuOnSelect={true}
                hideSelectedOptions={false}
                isClearable={false}
                placeholder="Filter Leaves"
                isSearchable={false}
                components={{
                  Option: CustomOption,
                  MultiValue,
                  ValueContainer: CustomValueContainer,
                  MultiValueRemove: NoRemove,
                }}
                styles={customStyles}
              />
            </div>
            <div className="member-filter review-rating-filter all-option custom-scroll">
              <Select
                options={yearsList}
                defaultValue={{
                  label: year,
                  value: year,
                }}
                className={["react-select-container"]}
                onChange={handleSelectedYear}
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <Link
            to={constants.routes.staff.applyLeaves.replace(
              ":officeId",
              encodeId(officeId),
            )}
          >
            <button
              className="button button-round button-shadow w-sm-100 "
              title={t("staffLeaves.applyLeave")}
            >
              {t("staffLeaves.applyLeave")}
            </button>
          </Link>
        </div>
        {renderListing()}
      </div>
      {selectedReason && (
        <CustomModal
          isOpen={!!selectedReason}
          setIsOpen={() => setSelectedReason(null)}
          title={t("staffLeaves.reason")}
          subTitle1={selectedReason}
        />
      )}
      {isChangesModalOpen && (
        <RejectionModal
          isRejectionModalOpen={t("modalForComment." + isChangesModalOpen)}
          setIsRejectionModalOpen={() => setIsChangesModalOpen("")}
          showLoader={showLoader}
          forLeave={true}
          onReject={(reason) => {
            switch (isChangesModalOpen) {
              case leaveTags.archive:
                applyArchiveActions(true, reason);
                break;
              case leaveTags.unarchive:
                applyArchiveActions(false, reason);
                break;
              case leaveTags.withdrawn:
                withdrawLeaves(reason);
                break;
              case leaveTags.cancel:
                staffCancelLeaves(reason);
                break;
              default:
                break;
            }
          }}
        />
      )}

      {confirmPopup && (
        <CancellationModal
          t={t}
          isModalOpen={confirmPopup}
          closeModal={handlePopup}
          leaveDetail={selectedLeave}
        />
      )}
    </>
  );
};

export default withTranslation()(Leaves);
